import React, { useEffect } from 'react';
import { Modal, Form, Input, Alert, Divider, Space, Typography } from 'antd';
import { useTheme } from '@/components/common/ThemeProvider';
import { NubAccessLevelDTO, NubPermissionDTO } from '../types';
import { LockOutlined, SafetyOutlined, KeyOutlined } from '@ant-design/icons';
import { PermissionSelector } from './PermissionSelector';

const { Text } = Typography;

interface AccessLevelFormDialogProps {
    open: boolean;
    mode: 'view' | 'add' | 'edit' | 'delete';
    initialData?: NubAccessLevelDTO | null;
    permissionCategories: string[];
    permissions: NubPermissionDTO[];
    selectedPermissions: string[];
    getPermissionsByCategory: (category: string) => NubPermissionDTO[];
    onPermissionChange: (permissions: string[]) => void;
    onCancel: () => void;
    onSubmit: (data: NubAccessLevelDTO) => Promise<void>;
}

interface AccessLevelFormData {
    code: string;
    name: string;
    description?: string;
}

export const AccessLevelFormDialog: React.FC<AccessLevelFormDialogProps> = ({
    open,
    mode,
    initialData,
    permissionCategories,
    permissions,
    selectedPermissions,
    getPermissionsByCategory,
    onPermissionChange,
    onCancel,
    onSubmit,
}) => {
    const [form] = Form.useForm<AccessLevelFormData>();
    const { dark } = useTheme();
    const [loading, setLoading] = React.useState(false);

    // Reset form when modal opens/closes or mode changes
    useEffect(() => {
        if (open && initialData) {
            form.setFieldsValue({
                code: initialData.code,
                name: initialData.name,
                description: initialData.description,
            });
        } else {
            form.resetFields();
        }
    }, [open, initialData, form]);

    // Handle form submission
    const handleSubmit = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();

            if (mode === 'delete') {
                await onSubmit(initialData as NubAccessLevelDTO);
            } else {
                // Transform form data into DTO
                const submitData: NubAccessLevelDTO = {
                    ...values,
                    permissions: selectedPermissions
                        .map(code => permissions.find(p => p.code === code))
                        .filter((p): p is NubPermissionDTO => p !== undefined)
                };
                await onSubmit(submitData);
            }
        } catch (error) {
            console.error('Form validation failed:', error);
        } finally {
            setLoading(false);
        }
    };


    const isReadOnly = mode === 'view' || mode === 'delete';

    const modalTitle = {
        view: 'View Access Level',
        add: 'Create New Access Level',
        edit: 'Edit Access Level',
        delete: 'Delete Access Level',
    }[mode];

    const modalProps = {
        view: {
            okButtonProps: { style: { display: 'none' } },
            cancelButtonProps: { children: 'Close' }
        },
        add: {
            okText: 'Create',
            okButtonProps: {
                type: 'primary',
                icon: <SafetyOutlined />
            }
        },
        edit: {
            okText: 'Save Changes',
            okButtonProps: {
                type: 'primary',
                icon: <SafetyOutlined />
            }
        },
        delete: {
            okText: 'Delete',
            okButtonProps: {
                danger: true,
                icon: <LockOutlined />
            },
        },
    }[mode];

    return (
        //  @ts-ignore
        <Modal
            title={modalTitle}
            open={open}
            onCancel={onCancel}
            onOk={handleSubmit}
            confirmLoading={loading}
            width={1200}
            {...modalProps}
            className={dark ? 'dark' : ''}
        >
            {mode === 'delete' ? (
                <Alert
                    message="Delete Access Level"
                    description={
                        <Space direction="vertical">
                            <span>Are you sure you want to delete this access level?</span>
                            <strong>Access Level: {initialData?.name} ({initialData?.code})</strong>
                            <span>This action cannot be undone and will:</span>
                            <ul className="list-disc ml-5">
                                <li>Remove all associated permissions</li>
                                <li>Remove this access level from all groups</li>
                                <li>Affect all users with groups using this access level</li>
                            </ul>
                        </Space>
                    }
                    type="error"
                    showIcon
                />
            ) : (
                <Form
                    form={form}
                    layout="vertical"
                    disabled={isReadOnly}
                >
                    <Divider orientation="left">Basic Information</Divider>

                    <Form.Item
                        label="Access Level Code"
                        name="code"
                        rules={[
                            { required: true, message: 'Please input access level code' },
                            { min: 3, message: 'Code must be at least 3 characters' },
                            {
                                pattern: /^[A-Z0-9_]+$/,
                                message: 'Code must contain only uppercase letters, numbers, and underscores'
                            }
                        ]}
                    >
                        <Input
                            prefix={<KeyOutlined />}
                            placeholder="Enter access level code"
                            readOnly={mode === 'edit'}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Name"
                        name="name"
                        rules={[
                            { required: true, message: 'Please input access level name' },
                            { min: 3, message: 'Name must be at least 3 characters' }
                        ]}
                    >
                        <Input
                            placeholder="Enter access level name"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Description"
                        name="description"
                    >
                        <Input.TextArea
                            placeholder="Enter description"
                            rows={3}
                        />
                    </Form.Item>

                    <Divider orientation="left">Permissions</Divider>

                    {isReadOnly ? (
                        <div className="space-y-4">
                            {permissionCategories.map(category => {
                                const categoryPermissions = getPermissionsByCategory(category);
                                const selectedCategoryPermissions = categoryPermissions.filter(
                                    // @ts-ignore
                                    p => selectedPermissions.includes(p.code)
                                );

                                if (selectedCategoryPermissions.length === 0) return null;

                                return (
                                    <div key={category} className="space-y-2">
                                        <Text strong>{category}</Text>
                                        <div className="pl-4">
                                            {selectedCategoryPermissions.map(permission => (
                                                <div key={permission.code} className="py-1">
                                                    <Text>{permission.name}</Text>
                                                    {permission.description && (
                                                        <div className="text-sm text-gray-500">
                                                            {permission.description}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <PermissionSelector
                            permissions={permissions}
                            selectedPermissions={selectedPermissions}
                            permissionCategories={permissionCategories}
                            getPermissionsByCategory={getPermissionsByCategory}
                            onPermissionChange={onPermissionChange}
                            disabled={isReadOnly}
                        />
                    )}
                </Form>
            )}
        </Modal>
    );
};